import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AddressDisplay } from "@/components/AddressDisplay";
import { ROLE_KEYS, ROLE_LABELS, ROLES } from "@/lib/web3";
import { CONSOLE } from "@/constants/testIds";
import { KeyRound, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";

const ROLE_BADGE = {
  [ROLES.DEFAULT_ADMIN_ROLE]: "bg-slate-900 text-white border-slate-900",
  [ROLES.UPGRADER_ROLE]: "bg-slate-100 text-slate-900 border-slate-300",
  [ROLES.MASTER_MINTER_ROLE]: "bg-emerald-50 text-emerald-700 border-emerald-200",
  [ROLES.MINTER_ROLE]: "bg-emerald-50 text-emerald-700 border-emerald-200",
  [ROLES.PAUSER_ROLE]: "bg-amber-50 text-amber-700 border-amber-200",
  [ROLES.BLACKLISTER_ROLE]: "bg-slate-100 text-slate-700 border-slate-300",
  [ROLES.BURNER_ROLE]: "bg-red-50 text-red-700 border-red-200",
};

export default function AccessControl() {
  const { roles, actions } = useApp();
  const [role, setRole] = useState(ROLES.MINTER_ROLE);
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const active = roles.filter((r) => r.granted);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Access Control</div>
        <h1 className="text-3xl font-semibold tracking-tight">Roles & permissions</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-slate-700" strokeWidth={1.75} />
          <h3 className="text-sm font-semibold tracking-tight">Grant role</h3>
        </div>
        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid={CONSOLE.roleSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_KEYS.map((k) => (
                  <SelectItem key={k} value={k}>{ROLE_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Address</Label>
            <Input data-testid={CONSOLE.roleAddressInput} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x… (multi-sig recommended)" className="font-mono" />
          </div>
          <div className="md:col-span-3 space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Note</Label>
            <Input data-testid={CONSOLE.roleNoteInput} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal label" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button
              data-testid={CONSOLE.roleGrantBtn}
              disabled={!address || busy}
              onClick={async () => {
                setBusy(true);
                try { await actions.doRole({ role, address, granted: true, note }); setAddress(""); setNote(""); }
                catch (e) { toast.error("Grant failed", { description: e.message }); }
                finally { setBusy(false); }
              }}
              className="w-full rounded-md bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
              Grant
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold tracking-tight">Role holders</h3>
          <Badge variant="outline" className="font-mono text-[10px]">{active.length} active</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Holder</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Note</TableHead>
              <TableHead className="text-xs uppercase tracking-widest text-slate-500">Updated</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest text-slate-500">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {active.map((r) => (
              <TableRow key={r.id} data-testid={`role-row-${r.role}-${r.address}`}>
                <TableCell>
                  <span className={`inline-flex items-center text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded ${ROLE_BADGE[r.role] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                    {ROLE_LABELS[r.role] || "Custom"}
                  </span>
                </TableCell>
                <TableCell><AddressDisplay value={r.address} short={false} /></TableCell>
                <TableCell className="text-sm text-slate-600">{r.note || "—"}</TableCell>
                <TableCell className="text-xs font-mono text-slate-500">{timeAgo(r.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={CONSOLE.roleRevokeBtn(r.role, r.address)}
                    variant="outline"
                    size="sm"
                    onClick={() => actions.doRole({ role: r.role, address: r.address, granted: false, note: r.note })}
                    className="rounded-md border-slate-200"
                  >
                    Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {active.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-sm text-slate-500 px-5 py-8 text-center">No roles granted yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
